import {
  emptyAddress,
  emptyBankAccount,
  emptyFinancialContact,
  type Address,
  type BankAccount,
  type BrokerChannelType,
  type BrokerCompanyData,
  type BrokerInput,
  type BrokerOnboardingInput,
  type BrokerOnboardingPerson,
  type FinancialContact,
} from '@/shared/api/resources';

/**
 * Assistente de cadastro de corretor (P-COR-2): as etapas do OnPoint, os campos
 * obrigatorios, a conferencia de cada etapa e o pedido do "Concluir cadastro".
 *
 * Fica fora do componente para que o mesmo assistente sirva a seguradora
 * (`/corretores/novo`) e o autocadastro da corretora (P-ACE-2): o que muda entre os
 * dois e quem pode o que, e nao as regras do formulario.
 */

export const etapasDoAssistente = [
  'cnpj',
  'corretora',
  'bancarios',
  'usuarios',
] as const;

export type EtapaDoAssistente = (typeof etapasDoAssistente)[number];

/** Os nomes das etapas no OnPoint. */
export const rotulosDasEtapas: Record<EtapaDoAssistente, string> = {
  cnpj: 'Buscar CNPJ',
  corretora: 'Dados da Corretora',
  bancarios: 'Dados Bancarios',
  usuarios: 'Dados do Usuario',
};

export interface PessoaDoAssistente {
  /** Chave local da linha, para a lista de usuarios. */
  chave: string;
  name: string;
  email: string;
  phone: string;
  role_id: string;
}

export interface CorretoraDoAssistente {
  legal_name: string;
  susep_code: string;
  municipal_registration: string;
  channel_type_id: string;
  insurer_broker_code: string;
  insurer_branch_code: string;
  insurer_contract_code: string;
  /** Recadastramento SUSEP, data AAAA-MM-DD. */
  reregistered_on: string;
  trade_name: string;
  email: string;
  phone: string;
  address: Address;
  // Vem da consulta a Receita, quando houver provedor, sem campo no assistente: o
  // OnPoint grava sem mostrar. Sem a consulta, ficam vazios e sao preenchidos na ficha.
  cnpj_status: string;
  cnae_code: string;
  legal_nature: string;
}

/**
 * O passo Dados do Usuario do cadastro publico com acesso na hora (ADR 0095): o
 * administrador do portal da corretora, com a senha escolhida na tela e a ciencia do
 * aviso de privacidade.
 */
export interface AcessoDoAssistente {
  name: string;
  email: string;
  cpf: string;
  phone: string;
  password: string;
  password_confirmation: string;
  ciente: boolean;
}

export interface DadosDoAssistente {
  cnpj: string;
  corretora: CorretoraDoAssistente;
  administrador: PessoaDoAssistente;
  conta: BankAccount;
  responsavel: FinancialContact;
  usuarios: PessoaDoAssistente[];
  /** So no cadastro publico com acesso na hora. */
  acesso: AcessoDoAssistente;
}

/** O que muda entre quem usa o assistente. */
export interface ContextoDoAssistente {
  /** corretores.dados_bancarios.update: sem ela, a conta fica para depois. */
  podeAlterarConta: boolean;
  /** Convidar e conceder perfil: sem elas, a corretora nasce sem pessoas. */
  podeConvidar: boolean;
  /** O canal escolhido pede os codigos da seguradora. */
  canalExigeCodigos: boolean;
  /** Ha canal para escolher. Sem nenhum, o canal nao e exigido. */
  haCanais: boolean;
  /**
   * Cadastro publico com acesso na hora (ADR 0095): sem administrador no passo Dados da
   * Corretora, e o passo Dados do Usuario cria o acesso com a senha.
   */
  acessoImediato?: boolean | undefined;
}

/** Tamanho minimo da senha, o mesmo que o servidor confere (PasswordPolicyMessage). */
export const SENHA_MINIMA_DO_ASSISTENTE = 12;

let sequenciaDePessoas = 0;

export function novaPessoaDoAssistente(roleId = ''): PessoaDoAssistente {
  sequenciaDePessoas += 1;
  return {
    chave: `pessoa-${sequenciaDePessoas}`,
    name: '',
    email: '',
    phone: '',
    role_id: roleId,
  };
}

export function dadosIniciaisDoAssistente(cnpj = ''): DadosDoAssistente {
  return {
    cnpj,
    corretora: {
      legal_name: '',
      susep_code: '',
      municipal_registration: '',
      channel_type_id: '',
      insurer_broker_code: '',
      insurer_branch_code: '',
      insurer_contract_code: '',
      reregistered_on: '',
      trade_name: '',
      email: '',
      phone: '',
      address: { ...emptyAddress },
      cnpj_status: '',
      cnae_code: '',
      legal_nature: '',
    },
    administrador: novaPessoaDoAssistente(),
    conta: { ...emptyBankAccount },
    responsavel: { ...emptyFinancialContact },
    usuarios: [],
    acesso: {
      name: '',
      email: '',
      cpf: '',
      phone: '',
      password: '',
      password_confirmation: '',
      ciente: false,
    },
  };
}

/**
 * Campos obrigatorios do assistente.
 *
 * O OnPoint recusa o passo com "Todos os campos precisam ser preenchidos" (COR-138).
 * Quais sao de fato obrigatorios esta a confirmar com a seguradora: a lista segue o
 * OnPoint e fica num lugar so, para mudar sem mexer na tela. Duas excecoes, pelo que o
 * SeguraCore tem a mais: o digito da agencia (ha banco sem ele; o OnPoint mostra um
 * "Digito" so) e o celular do usuario, que o assistente da seguradora no OnPoint nao
 * pede. A inscricao municipal tambem e opcional, por decisao do dono de 29/10/2026: a
 * corretora que nao a informa segue o cadastro. Os dados complementares da Receita
 * (nome fantasia, contato e endereco) sao opcionais. Ver
 * docs/ADR/0038-assistente-de-cadastro-do-corretor.md.
 */
export const obrigatoriosDoAssistente = {
  corretora: ['legal_name', 'susep_code', 'channel_type_id', 'reregistered_on'],
  codigosDaSeguradora: [
    'insurer_broker_code',
    'insurer_branch_code',
    'insurer_contract_code',
  ],
  administrador: ['name', 'email', 'phone', 'role_id'],
  conta: ['bank_code', 'account_type', 'agency', 'account', 'account_digit'],
  responsavel: ['name', 'document', 'email', 'phone'],
  usuario: ['name', 'email', 'role_id'],
} satisfies {
  corretora: (keyof CorretoraDoAssistente)[];
  codigosDaSeguradora: (keyof CorretoraDoAssistente)[];
  administrador: (keyof PessoaDoAssistente)[];
  conta: (keyof BankAccount)[];
  responsavel: (keyof FinancialContact)[];
  usuario: (keyof PessoaDoAssistente)[];
};

/** Erros por campo, com o nome do campo do servidor ("portal_admin.email"). */
export type ErrosDoAssistente = Record<string, string>;

export const MENSAGEM_TODOS_OS_CAMPOS = 'Todos os campos precisam ser preenchidos';
const OBRIGATORIO = 'Campo obrigatorio.';

export function soDigitosDoAssistente(valor: string): string {
  return valor.replace(/\D/g, '');
}

function todosIguais(digitos: string): boolean {
  return digitos.split('').every((digito) => digito === digitos[0]);
}

/** CNPJ pelo digito verificador, como o servidor confere. */
export function cnpjValidoDoAssistente(valor: string): boolean {
  const digitos = soDigitosDoAssistente(valor);
  if (digitos.length !== 14 || todosIguais(digitos)) return false;
  const digito = (base: string, pesos: number[]) => {
    const soma = pesos.reduce((total, peso, i) => total + Number(base[i]) * peso, 0);
    const resto = soma % 11;
    return resto < 2 ? 0 : 11 - resto;
  };
  const primeiro = digito(digitos.slice(0, 12), [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]);
  const segundo = digito(digitos.slice(0, 13), [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]);
  return Number(digitos[12]) === primeiro && Number(digitos[13]) === segundo;
}

/** CPF pelo digito verificador, como o servidor confere. */
export function cpfValidoDoAssistente(valor: string): boolean {
  const digitos = soDigitosDoAssistente(valor);
  if (digitos.length !== 11 || todosIguais(digitos)) return false;
  const digito = (base: string, peso: number) => {
    let soma = 0;
    for (let i = 0; i < base.length; i += 1) soma += Number(base[i]) * (peso - i);
    const resto = (soma * 10) % 11;
    return resto === 10 ? 0 : resto;
  };
  return (
    Number(digitos[9]) === digito(digitos.slice(0, 9), 10) &&
    Number(digitos[10]) === digito(digitos.slice(0, 10), 11)
  );
}

export function emailValidoDoAssistente(valor: string): boolean {
  return /^[^@\s]+@[^@\s.]+(\.[^@\s.]+)+$/.test(valor.trim());
}

/** Celular ou telefone com DDD: 10 ou 11 digitos, como o convite confere. */
export function telefoneValidoDoAssistente(valor: string): boolean {
  const digitos = soDigitosDoAssistente(valor);
  return digitos.length === 10 || digitos.length === 11;
}

function vazio(valor: string): boolean {
  return valor.trim() === '';
}

function confereObrigatorios<T extends object>(
  objeto: T,
  campos: readonly (keyof T)[],
  prefixo: string,
  erros: ErrosDoAssistente,
) {
  for (const campo of campos) {
    const valor = objeto[campo];
    if (typeof valor === 'string' && vazio(valor)) {
      erros[`${prefixo}${String(campo)}`] = OBRIGATORIO;
    }
  }
}

function conferePessoa(
  pessoa: PessoaDoAssistente,
  campos: readonly (keyof PessoaDoAssistente)[],
  prefixo: string,
  erros: ErrosDoAssistente,
) {
  confereObrigatorios(pessoa, campos, prefixo, erros);
  if (!vazio(pessoa.email) && !emailValidoDoAssistente(pessoa.email)) {
    erros[`${prefixo}email`] = 'E-mail invalido';
  }
  if (!vazio(pessoa.phone) && !telefoneValidoDoAssistente(pessoa.phone)) {
    erros[`${prefixo}phone`] = 'Telefone invalido';
  }
}

/**
 * Confere uma etapa antes de seguir.
 *
 * A conferencia do servidor continua valendo: esta existe para a pessoa saber o que
 * falta antes de chegar ao fim, como no OnPoint, que recusa o passo.
 */
export function confereEtapaDoAssistente(
  etapa: EtapaDoAssistente,
  dados: DadosDoAssistente,
  contexto: ContextoDoAssistente,
): ErrosDoAssistente {
  const erros: ErrosDoAssistente = {};
  switch (etapa) {
    case 'cnpj':
      if (vazio(dados.cnpj)) erros.document = OBRIGATORIO;
      else if (!cnpjValidoDoAssistente(dados.cnpj)) erros.document = 'CNPJ invalido';
      break;
    case 'corretora': {
      const exigidos = obrigatoriosDoAssistente.corretora.filter(
        (campo) => campo !== 'channel_type_id' || contexto.haCanais,
      );
      confereObrigatorios(dados.corretora, exigidos, '', erros);
      if (erros.channel_type_id) {
        erros.channel_type_id = "Campo 'Selecione o canal' precisa ser preenchido";
      }
      if (contexto.canalExigeCodigos) {
        confereObrigatorios(
          dados.corretora,
          obrigatoriosDoAssistente.codigosDaSeguradora,
          '',
          erros,
        );
      }
      if (
        !vazio(dados.corretora.email) &&
        !emailValidoDoAssistente(dados.corretora.email)
      ) {
        erros.email = 'E-mail invalido';
      }
      if (contexto.podeConvidar) {
        conferePessoa(
          dados.administrador,
          obrigatoriosDoAssistente.administrador,
          'portal_admin.',
          erros,
        );
      }
      break;
    }
    case 'bancarios':
      if (contexto.podeAlterarConta) {
        confereObrigatorios(
          dados.conta,
          obrigatoriosDoAssistente.conta,
          'bank_account.',
          erros,
        );
      }
      confereObrigatorios(
        dados.responsavel,
        obrigatoriosDoAssistente.responsavel,
        'financial_contact.',
        erros,
      );
      if (
        !vazio(dados.responsavel.document) &&
        !cpfValidoDoAssistente(dados.responsavel.document)
      ) {
        erros['financial_contact.document'] = 'CPF invalido';
      }
      if (
        !vazio(dados.responsavel.email) &&
        !emailValidoDoAssistente(dados.responsavel.email)
      ) {
        erros['financial_contact.email'] = 'E-mail invalido';
      }
      if (
        !vazio(dados.responsavel.phone) &&
        !telefoneValidoDoAssistente(dados.responsavel.phone)
      ) {
        erros['financial_contact.phone'] = 'Telefone invalido';
      }
      break;
    case 'usuarios': {
      if (contexto.acessoImediato) {
        conferirAcesso(dados.acesso, erros);
        break;
      }
      if (!contexto.podeConvidar) break;
      const vistos = new Set<string>();
      if (!vazio(dados.administrador.email)) {
        vistos.add(dados.administrador.email.trim().toLowerCase());
      }
      dados.usuarios.forEach((usuario, indice) => {
        const prefixo = `users[${indice}].`;
        conferePessoa(usuario, obrigatoriosDoAssistente.usuario, prefixo, erros);
        const email = usuario.email.trim().toLowerCase();
        if (email && vistos.has(email) && !erros[`${prefixo}email`]) {
          erros[`${prefixo}email`] = 'Este e-mail ja foi informado neste cadastro.';
        }
        if (email) vistos.add(email);
      });
      break;
    }
  }
  return erros;
}

/** Confere o passo Dados do Usuario do cadastro publico. */
function conferirAcesso(acesso: AcessoDoAssistente, erros: ErrosDoAssistente) {
  const obrigatorios: [keyof AcessoDoAssistente, string][] = [
    ['name', 'user.name'],
    ['email', 'user.email'],
    ['cpf', 'user.cpf'],
    ['phone', 'user.phone'],
    ['password', 'user.password'],
    ['password_confirmation', 'user.password_confirmation'],
  ];
  for (const [chave, campo] of obrigatorios) {
    const valor = acesso[chave];
    if (typeof valor === 'string' && vazio(valor)) erros[campo] = OBRIGATORIO;
  }
  if (!vazio(acesso.email) && !emailValidoDoAssistente(acesso.email)) {
    erros['user.email'] = 'E-mail invalido';
  }
  if (!vazio(acesso.cpf) && !cpfValidoDoAssistente(acesso.cpf)) {
    erros['user.cpf'] = 'CPF invalido';
  }
  if (!vazio(acesso.phone) && !telefoneValidoDoAssistente(acesso.phone)) {
    erros['user.phone'] = 'Telefone invalido';
  }
  if (
    !vazio(acesso.password) &&
    [...acesso.password].length < SENHA_MINIMA_DO_ASSISTENTE
  ) {
    erros['user.password'] = 'A senha precisa ter de 12 a 256 caracteres.';
  }
  if (
    !vazio(acesso.password_confirmation) &&
    acesso.password_confirmation !== acesso.password
  ) {
    erros['user.password_confirmation'] = 'A confirmacao nao confere com a senha.';
  }
  if (!acesso.ciente) {
    erros.privacy_acknowledged = 'Confirme a ciencia do aviso de privacidade.';
  }
}

/** A etapa em que o campo da recusa do servidor esta. */
export function etapaDoCampoDoAssistente(campo: string): EtapaDoAssistente {
  if (campo === 'document') return 'cnpj';
  if (campo.startsWith('user.') || campo === 'privacy_acknowledged') return 'usuarios';
  if (
    campo.startsWith('bank_account') ||
    campo.startsWith('financial_contact') ||
    campo.startsWith('pix_')
  ) {
    return 'bancarios';
  }
  if (campo.startsWith('users')) return 'usuarios';
  return 'corretora';
}

/** Preenche o que a consulta a Receita devolveu, sem apagar o que ja foi digitado. */
export function aplicaDadosDaReceita(
  corretora: CorretoraDoAssistente,
  receita: BrokerCompanyData,
): CorretoraDoAssistente {
  const ou = (atual: string, novo: string) => (vazio(atual) ? novo : atual);
  const enderecoVazio = Object.values(corretora.address).every(
    (valor) => typeof valor !== 'string' || vazio(valor),
  );
  return {
    ...corretora,
    legal_name: ou(corretora.legal_name, receita.legal_name),
    trade_name: ou(corretora.trade_name, receita.trade_name),
    email: ou(corretora.email, receita.email),
    phone: ou(corretora.phone, receita.phone),
    cnpj_status: ou(corretora.cnpj_status, receita.cnpj_status),
    cnae_code: ou(corretora.cnae_code, receita.cnae_code),
    legal_nature: ou(corretora.legal_nature, receita.legal_nature),
    address: enderecoVazio
      ? { ...emptyAddress, ...receita.address }
      : corretora.address,
  };
}

function pessoaDoPedido(pessoa: PessoaDoAssistente): BrokerOnboardingPerson {
  const saida: BrokerOnboardingPerson = {
    name: pessoa.name.trim(),
    email: pessoa.email.trim(),
    role_ids: pessoa.role_id ? [pessoa.role_id] : [],
  };
  if (!vazio(pessoa.phone)) saida.phone = pessoa.phone.trim();
  return saida;
}

function preenchido(objeto: object): boolean {
  return Object.values(objeto).some(
    (valor) => typeof valor === 'string' && !vazio(valor),
  );
}

/**
 * Monta o "Concluir cadastro".
 *
 * Quem nao pode trocar a conta nao a envia, e quem nao pode convidar nao envia
 * pessoas: o servidor recusaria, e a corretora nasce sem elas, para quem tem a
 * permissao completar depois.
 */
export function pedidoDoAssistente(
  dados: DadosDoAssistente,
  contexto: ContextoDoAssistente,
): BrokerOnboardingInput {
  const c = dados.corretora;
  const broker: BrokerInput = {
    document: soDigitosDoAssistente(dados.cnpj),
    legal_name: c.legal_name.trim(),
    susep_code: c.susep_code.trim(),
    municipal_registration: c.municipal_registration.trim(),
    trade_name: c.trade_name.trim(),
    email: c.email.trim(),
    phone: c.phone.trim(),
  };
  if (c.channel_type_id) broker.channel_type_id = c.channel_type_id;
  if (contexto.canalExigeCodigos) {
    broker.insurer_broker_code = c.insurer_broker_code.trim();
    broker.insurer_branch_code = c.insurer_branch_code.trim();
    broker.insurer_contract_code = c.insurer_contract_code.trim();
  }
  if (c.reregistered_on) broker.reregistered_on = c.reregistered_on;
  if (c.cnpj_status)
    broker.cnpj_status = c.cnpj_status as NonNullable<BrokerInput['cnpj_status']>;
  if (c.cnae_code) broker.cnae_code = c.cnae_code;
  if (c.legal_nature) broker.legal_nature = c.legal_nature;
  if (preenchido(c.address)) broker.address = c.address;
  if (contexto.podeAlterarConta && preenchido(dados.conta))
    broker.bank_account = dados.conta;
  if (preenchido(dados.responsavel)) broker.financial_contact = dados.responsavel;

  const pedido: BrokerOnboardingInput = { broker };
  if (contexto.podeConvidar) {
    if (preenchido({ ...dados.administrador, chave: '', role_id: '' })) {
      pedido.portal_admin = pessoaDoPedido(dados.administrador);
    }
    pedido.users = dados.usuarios.map(pessoaDoPedido);
  }
  return pedido;
}

/**
 * Canais oferecidos no assistente.
 *
 * O OnPoint pede ao abrir o assistente os canais "SomenteCadastro". Se a empresa
 * marcou canais para o cadastro, o assistente mostra so esses; sem nenhum marcado,
 * todos os ativos. O significado exato do SomenteCadastro esta a confirmar.
 */
export function canaisDoAssistente(canais: BrokerChannelType[]): BrokerChannelType[] {
  const ativos = canais.filter((canal) => canal.active);
  const doCadastro = ativos.filter((canal) => canal.registration_only);
  return doCadastro.length > 0 ? doCadastro : ativos;
}

// --- Autocadastro publico (P-ACE-2) ---------------------------------------------

/**
 * Retoma o rascunho do autocadastro no assistente.
 *
 * O rascunho e o pedido do assistente gravado; aqui ele volta para os campos da tela.
 * O CNPJ e sempre o do inicio do cadastro.
 */
export function dadosDoRascunhoDoAssistente(
  cnpj: string,
  rascunho: Partial<BrokerOnboardingInput> | undefined,
): DadosDoAssistente {
  const dados = dadosIniciaisDoAssistente(cnpj);
  const b = rascunho?.broker;
  if (b) {
    const texto = (valor: unknown) => (typeof valor === 'string' ? valor : '');
    dados.corretora = {
      ...dados.corretora,
      legal_name: texto(b.legal_name),
      susep_code: texto(b.susep_code),
      municipal_registration: texto(b.municipal_registration),
      channel_type_id: texto(b.channel_type_id),
      insurer_broker_code: texto(b.insurer_broker_code),
      insurer_branch_code: texto(b.insurer_branch_code),
      insurer_contract_code: texto(b.insurer_contract_code),
      reregistered_on: texto(b.reregistered_on),
      trade_name: texto(b.trade_name),
      email: texto(b.email),
      phone: texto(b.phone),
      address: { ...dados.corretora.address, ...(b.address ?? {}) },
    };
    if (b.bank_account) dados.conta = { ...dados.conta, ...b.bank_account };
    if (b.financial_contact) {
      dados.responsavel = { ...dados.responsavel, ...b.financial_contact };
    }
  }
  const pessoa = (p: BrokerOnboardingPerson): PessoaDoAssistente => ({
    ...novaPessoaDoAssistente(p.role_ids[0] ?? ''),
    name: p.name,
    email: p.email,
    phone: p.phone ?? '',
  });
  if (rascunho?.portal_admin) dados.administrador = pessoa(rascunho.portal_admin);
  if (rascunho?.users) dados.usuarios = rascunho.users.map(pessoa);
  return dados;
}

/**
 * O pedido do autocadastro: so os campos que a rota publica aceita. Os que vem da
 * consulta a Receita (situacao do CNPJ, CNAE, natureza juridica) ficam com a
 * seguradora, que os confere na ficha depois da aprovacao.
 */
export function pedidoPublicoDoAssistente(
  pedido: BrokerOnboardingInput,
): BrokerOnboardingInput {
  const broker = { ...pedido.broker };
  delete broker.cnpj_status;
  delete broker.cnae_code;
  delete broker.legal_nature;
  return { ...pedido, broker };
}
